import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import SurveyResults from '../../components/common/SurveyResults';
import SendComposeModal from '../../components/common/SendComposeModal';
import EventSocial from '../../components/common/EventSocial';
import { TagPills } from '../../components/common/EventTags';

import { apiRequest, downloadApiFile, shortDate, wallClockTime } from '../../utils';
import './MemberHome.css';
import './CompanyEvents.css';
import './HostEvent.css';

/* Running the event — the screen the host stands in front of.

   Everything else about a company event is planning: create it, publish it, chase answers. This
   is the hour it happens. The cards are in the order the night runs: who is running it, what is
   on next, what the room is answering, how it splits into groups, and — afterwards — the wall
   it left behind. The counts sit above all of them because they are the first thing anybody
   standing there looks at.

   Two people open it and they are not the same person. The engagement ADMIN owns the event. The
   internal HOST is an employee attached to it who runs it — release a survey, chase the silent,
   edit the run sheet, scan people in — and edits it, on the editor screen. What stays the
   admin's: appointing a co-host, deleting the event, mailing the roster. Writing the SURVEYS
   moved across to the host (`can_manage_surveys`) — on a Lab there is no admin to write them.
   `can_manage` comes from the server and every write is re-checked there; the flag only decides
   what is worth drawing.

   ★ A third person opens the EDITOR and never this screen: the external host, the facilitator we
   send in. That is why the COG in the header leads to /host-event/:id/settings and not to the
   editor. What kind of event this is, whether people may register, whether it is published,
   cancelled or deleted — those belong to the company that owns the event, not to whoever we sent
   to stand in front of the room, so they sit behind this screen's gate rather than the editor's.
   See HostEventSettings and routes/company_events.

   ★ Results are always this event's. A satisfaction survey reused all year holds every event's
   answers; "how did tonight go" is the per-event aggregate and nothing else. */

// How often the projector refreshes. Slow enough not to hammer the API from a laptop left open
// all evening, fast enough that a room watching its own answers appear feels live.
const PRESENT_POLL_MS = 6000;

const PHASE_ICONS = { pre: 'clock', live: 'megaphone', post: 'comment' };

/* When a survey is asked, in the order it happens. Mirrors shared_models
   mutations/event_surveys.PHASES; an empty phase is legal too and means "no moment". */
const PHASES = ['pre', 'live', 'post'];

/* Before / during / after, picked as three buttons rather than a dropdown.
   It is asked at the moment a survey is attached — the phase decides what the survey DOES
   (a `pre` one is put in front of people the second they register), so leaving it until
   later means the first registrations miss it. */
const PhaseChoice = ({ value, onChange, disabled, t }) => (
  <>
    <p className="bravos-cal__label">{t('surveyWhen')}</p>
    <div className="ce-tabs">
      {PHASES.map((p) => (
        <button
          key={p}
          type="button"
          disabled={disabled}
          className={`ce-tab ${value === p ? 'is-on' : ''}`}
          onClick={() => onChange(p)}
        >
          <Icon name={PHASE_ICONS[p]} /> {t(`phase_${p}`)}
        </button>
      ))}
    </div>
    <p className="bravos-q__hint mb-2">{t(`surveyWhenHint_${value}`)}</p>
  </>
);

/* What a new run-sheet line opens as.

   Nobody types a date standing in front of a room. The first line of a day starts when the event
   itself does — its own hour, on whichever day is on screen — and every line after it starts when
   the previous one ended. The end mirrors the start, so all that is ever left to type is an hour.
   Slots arrive in time order, so the last one on the day is the latest one. */
const nextSlot = (day, slots, event) => {
  const last = slots.length ? slots[slots.length - 1] : null;
  const hhmm = ((event && event.date) || '').slice(11, 16) || '09:00';
  const start = (last && (last.end || last.start))
    || (day ? `${day}T${hhmm}` : (event && event.date) || '');
  return { title: '', start, end: start };
};

/* The end follows the start until somebody moves it. Mirroring only ever touches an end that is
   still exactly the start it was prefilled with — a typed 10:30 stays 10:30. */
const withStart = (slot, start) => ({
  ...slot, start, end: slot.end === slot.start ? start : slot.end,
});

/* --- the screen ------------------------------------------------------------------------- */

const HostEvent = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { eventId } = useParams();
  const [params] = useSearchParams();
  // ★ Same reason as the survey library: `usePersistUrlParams` strips query params a tick after
  // they appear, so ?present= could never survive the click that set it. State, with the URL read
  // once at mount for a link that arrives carrying one.
  const [presenting, setPresenting] = useState(() => {
    const stashed = sessionStorage.getItem('present');
    if (stashed) sessionStorage.removeItem('present');
    return params.get('present') || stashed || null;
  });

  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [openSurvey, setOpenSurvey] = useState(null);
  const [results, setResults] = useState({});
  const [draftSlot, setDraftSlot] = useState(null);
  const [editSlot, setEditSlot] = useState(null);
  // How many groups the one-press card splits the room into. Four is the number a facilitator
  // reaches for by reflex; anything else is one tap away.
  const [boCount, setBoCount] = useState(4);
  const [day, setDay] = useState(null);
  // 'host' | 'survey_new' | 'survey_existing'. The two survey flows are separate because they are
  // separate questions: writing a new set of questions, or reusing one the company already has.
  const [picking, setPicking] = useState(null);
  const [people, setPeople] = useState(null);
  const [library, setLibrary] = useState(null);
  const [newSurvey, setNewSurvey] = useState('');
  // Defaults to 'pre': the commonest thing a host wants is to ask something before people arrive,
  // and it is the only phase with a deadline attached to it (registration).
  const [newPhase, setNewPhase] = useState('pre');
  // {surveyId, segment} the compose modal is open for, or null. The segment travels with it
  // because the two ways in mean different audiences: chasing the silent asks whoever has not
  // answered, while "how was it" asks the people who were actually in the room.
  const [composing, setComposing] = useState(null);

  const load = useCallback(() => {
    apiRequest('GET', `/company/event/${eventId}/host`)
      .then((res) => {
        setData(res);
        setDay((d) => d || (res.planning_days || [])[0] || null);
      })
      // Not the admin, not the host, or not this company's event — none of which this screen can
      // do anything about. The company calendar is where you belong; a host with no company (a
      // public Lab is nobody's) has no calendar, so they go back to the event itself.
      .catch(() => navigate(`/public-event/${eventId}`));
  }, [eventId, navigate]);

  useEffect(() => { load(); }, [load]);

  const loadResults = useCallback((surveyId) => {
    apiRequest('GET', `/company/event/${eventId}/surveys/${surveyId}/results`)
      .then((res) => setResults((r) => ({ ...r, [surveyId]: res })))
      .catch(() => setResults((r) => ({ ...r, [surveyId]: null })));
  }, [eventId]);

  const toggleResults = (surveyId) => {
    if (openSurvey === surveyId) { setOpenSurvey(null); return; }
    setOpenSurvey(surveyId);
    loadResults(surveyId);
  };

  const act = (promise) => {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    promise
      .then(() => load())
      .catch((err) => setNotice(t(err.message, { defaultValue: t('saveFailed') })))
      .finally(() => setBusy(false));
  };

  /* --- projector ------------------------------------------------------------------------ */

  // Kept in a ref so the interval never restarts when the results change — a restarting timer is
  // how a projector ends up refreshing every second by the end of the evening.
  const loadRef = useRef(loadResults);
  loadRef.current = loadResults;
  useEffect(() => {
    if (!presenting) return undefined;
    loadRef.current(presenting);
    const id = setInterval(() => loadRef.current(presenting), PRESENT_POLL_MS);
    return () => clearInterval(id);
  }, [presenting]);

  const surveys = useMemo(() => (data && data.surveys) || [], [data]);

  /* The post-survey to offer, once the event is actually over. `has_ended` comes from the
     server: the wire times are event-LOCAL wall clock, so the browser cannot decide this
     without being wrong by the offset between its zone and the event's.
     Unpublished ones are skipped — the mail's only button would go nowhere, and the row above
     already says "Not published". */
  const afterSurvey = useMemo(() => {
    const ended = data && data.event && data.event.has_ended;
    if (!ended) return null;
    return surveys.find((s) => s.phase === 'post' && s.is_published && s.link) || null;
  }, [data, surveys]);

  if (presenting) {
    const shown = surveys.find((s) => s.id === presenting);
    return (
      <div className="hv-present">
        <button
          type="button"
          className="hv-present__exit"
          onClick={() => setPresenting(null)}
        >
          <Icon name="cross-circle" /> {t('leavePresentMode')}
        </button>
        <h1 className="hv-present__title">{(shown && shown.name) || t('results')}</h1>
        {/* What is at stake, big enough to read from the back. The per-question points are in
            the results below; this is the total, for the room deciding whether to bother. */}
        {shown && shown.points > 0 && (
          <p className="hv-present__sub">
            {t('quizWorth', { n: shown.points })}
            {shown.quiz_fields > 0 && ` · ${t('nScoredQuestions', { n: shown.quiz_fields })}`}
          </p>
        )}
        <div className="hv-present__body">
          <SurveyResults results={results[presenting]} t={t} />
        </div>
      </div>
    );
  }

  /* ★ The wait is DRAWN, not blank.

     This screen is one payload, and on a busy event that payload is not instant. What it used to
     show meanwhile was a lone `auth__spinner` — a white ring built to sit inside a primary
     button, so on a white card it is invisible — and nothing else, because every card below is
     gated on the data. A host opening it read the screen as broken rather than as loading, which
     is the complaint that got this looked at.

     So: the shape of what is coming. The four counts and the cards under them, shimmering, with
     the Back button live from the first frame so a mistaken click is one press to undo. */
  if (data === null) {
    return (
      <Base activeNav="home">
        <Card>
          <Back to={`/company-event/${eventId}/edit`} />
          <Title my="1" className="mt-3">
            <Icon name="microphone" theme="primary" className="me-2" />
            {t('hostEvent')}
          </Title>
          <p className="bravos-q__hint mb-2">{t('loading')}</p>
          <div className="hv-counts">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="hv-count">
                <span className="hv-skel hv-skel--n" />
                <span className="hv-skel hv-skel--label" />
              </div>
            ))}
          </div>
        </Card>
        {[0, 1, 2].map((i) => (
          <Card key={i}>
            <div className="hv-skel hv-skel--title" />
            <div className="hv-skel hv-skel--line" />
            <div className="hv-skel hv-skel--line hv-skel--short" />
          </Card>
        ))}
      </Base>
    );
  }

  const event = data.event || {};
  const counts = data.counts || {};
  const canManage = !!data.can_manage;
  /* ★ Writing the questions is NOT `can_manage`. A Lab has no organising company, so it has no
     engagement admin, so `can_manage` is False on it for everybody — and the survey section drew
     its buttons on that flag, which left the person appointed to run the evening looking at an
     empty list with no way to fill it. The server answers the narrower question separately and
     re-derives it on every write. */
  const canWriteSurveys = !!data.can_manage_surveys;
  const hosts = data.hosts || [];
  const planning = data.planning || {};
  const breakouts = data.breakouts || [];
  // Three counts, never the prizes themselves — the awards screen holds those.
  const awards = data.awards || { total: 0, decided: 0, revealed: 0 };
  const days = data.planning_days || [];
  const slots = planning[day] || planning[''] || [];

  const when = [shortDate(event.date, i18n.language), wallClockTime(event.date)]
    .filter(Boolean).join(' · ');

  return (
    <Base activeNav="home">
      <Card>
        <Back to={`/company-event/${eventId}/edit`} />
        {/* The cog is the only thing on this screen that is not about tonight: what kind of
            event this is, who may register, published / cancelled / deleted. It lives up here
            as one small button rather than as a card at the bottom, because the person holding
            a microphone should not have to scroll past a delete button to reach the run sheet. */}
        <div className="hv-head">
          <Title my="1" className="mt-3">
            <Icon name="microphone" theme="primary" className="me-2" />
            {event.name || t('hostEvent')}
          </Title>
          <button
            type="button"
            className="hv-cog"
            title={t('eventSettings')}
            aria-label={t('eventSettings')}
            onClick={() => navigate(`/host-event/${eventId}/settings`)}
          >
            <Icon name="settings" />
          </button>
        </div>
        <p className="bravos-q__hint mb-2">{when}</p>
        <TagPills tags={event.tags} />

        {/* The numbers of the night. "Showed up" reads 0 until somebody scans, which is
            correct and is why the scanner sits right under it.

            ★ Two of them need a ROSTER — a set of people who could have come — and a public
            event has none: anybody may buy a seat, so "not registered" and "declined" are not
            zero, they are unanswerable. They are left out entirely rather than drawn as 0,
            which would read as "nobody is missing" and is the more confident lie. A company
            event, and an invite-only event with its guest list, still show all four. */}
        {/* ★ The two numbers about the ROOM are doors. "Coming" and "showed up" are the only
            two the host acts on while the event runs, and until now they were figures with
            nothing behind them — the names sat on a screen an employee host could not open at
            all. Both lead to the same place, because "who is coming" and "who is here" are one
            list read at two moments. The other tiles stay plain: there is no screen behind
            "declined" worth a tap mid-event. */}
        <div className="hv-counts">
          {[['registered', counts.registered, true], ['attended', counts.attended, true],
            ...(data.has_roster
              ? [['not_registered', counts.not_registered], ['declined', counts.declined]]
              : [['no_show', counts.no_show]])]
            .map(([key, n, isDoor]) => (isDoor ? (
              <button
                key={key}
                type="button"
                className="hv-count hv-count--link"
                onClick={() => navigate(`/host-event/${eventId}/door`)}
              >
                <span className="hv-count__n">{n != null ? n : '–'}</span>
                <span className="hv-count__label">{t(`segment_${key}`)}</span>
              </button>
            ) : (
              <div key={key} className="hv-count">
                <span className="hv-count__n">{n != null ? n : '–'}</span>
                <span className="hv-count__label">{t(`segment_${key}`)}</span>
              </div>
            )))}
        </div>

        <div className="bravos-form__row mt-2">
          <button
            type="button"
            className="bravos-copy"
            onClick={() => navigate(`/crew-scan?event=${eventId}`)}
          >
            <Icon name="qrcode" /> {t('scanAttendance')}
          </button>
          {/* The two things a scanner cannot do, next to it rather than hidden behind the count:
              the scan fails, or somebody arrives who was never on any list. Ungated — this is
              what running the event IS, and on a Lab there is no admin to hand it to. */}
          <button
            type="button"
            className="bravos-copy"
            onClick={() => navigate(`/host-event/${eventId}/door`)}
          >
            <Icon name="user-check" /> {t('markAttendanceManually')}
          </button>
          <button
            type="button"
            className="bravos-copy"
            onClick={() => navigate(`/host-event/${eventId}/door?add=1`)}
          >
            <Icon name="user-add" /> {t('addWalkIn')}
          </button>
          {canManage && (
            <button
              type="button"
              className="bravos-copy"
              onClick={() => navigate(`/company-event/${eventId}/people`)}
            >
              <Icon name="users" /> {t('whoIsComing')}
            </button>
          )}
        </div>
        {notice && <p className="bravos-cal__notice">{notice}</p>}
      </Card>

      {composing && (
        <SendComposeModal
          eventId={eventId}
          kind="survey"
          segment={composing.segment}
          surveyId={composing.surveyId}
          title={t('remindPending')}
          sendPath={`/company/event/${eventId}/surveys/${composing.surveyId}/remind`}
          onClose={() => setComposing(null)}
          onSent={(res) => {
            setNotice(t('sendSummary', { mailed: res.mailed || 0, n: res.notified || 0 }));
            load();
          }}
        />
      )}

      {/* --- hosts ------------------------------------------------------------------------- */}
      {canManage && (
        <Card>
          <Title my="1">
            <Icon name="microphone" theme="primary" className="me-2" />
            {t('hostHosts')}
          </Title>
          <p className="bravos-q__hint mb-2">{t('hostHostsNote')}</p>

          {hosts.length === 0 && <p className="ce-note">{t('noHostYet')}</p>}
          {hosts.map((h) => (
            <div key={h.host_id} className="ce-person">
              <span className="ce-person__body">
                <span className="ce-person__name">
                  {h.name || h.email}
                  {/* Which of the three rungs. The top one is the ordinary case on this screen —
                      it is what your own picker below appoints — so only the two below it are
                      labelled; a tag on every row is a tag nobody reads. */}
                  {h.access_level !== 'run' && (
                    <span className="ce-person__tag">
                      {t(h.access_level === 'edit' ? 'hostAccessEdit' : 'hostAccessHost')}
                    </span>
                  )}
                </span>
                <span className="ce-person__email">{h.email}</span>
              </span>
              <button
                type="button"
                className="ce-person__act"
                disabled={busy}
                onClick={() => act(apiRequest('DELETE',
                  `/company/event/${eventId}/hosts/${h.host_id}`))}
              >
                {t('removeHost')}
              </button>
            </div>
          ))}
          {/* Somebody on a lower rung is named on the event and runs the room; this screen is not
              theirs to open. Said once under the list rather than on every row. */}
          {hosts.some((h) => h.access_level !== 'run') && (
            <p className="ce-note">{t('hostAccessNote')}</p>
          )}

          {picking === 'host' ? (
            <div className="bravos-form">
              {(people || []).filter((p) => !hosts.some((h) => h.id === p.id)).map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className="bravos-submit bravos-submit--ghost"
                  disabled={busy}
                  onClick={() => { setPicking(null);
                    act(apiRequest('POST', `/company/event/${eventId}/hosts`,
                                   { partner_id: p.id })); }}
                >
                  {p.name || p.email}
                </button>
              ))}
              <button type="button" className="bravos-submit bravos-submit--ghost"
                      onClick={() => setPicking(null)}>
                {t('cancel')}
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="bravos-submit bravos-submit--ghost"
              onClick={() => {
                setPicking('host');
                if (people === null) {
                  apiRequest('GET', `/company/event/${eventId}/people-picker`)
                    .then((res) => setPeople(res.people || []))
                    .catch(() => setPeople([]));
                }
              }}
            >
              <Icon name="plus" /> {t('addHost')}
            </button>
          )}
        </Card>
      )}

      {/* --- run sheet --------------------------------------------------------------------- */}
      <Card>
        <Title my="1">
          <Icon name="clock" theme="primary" className="me-2" />
          {t('hostRunSheet')}
        </Title>

        {days.length > 1 && (
          <div className="ce-tabs">
            {days.map((d) => (
              <button
                key={d}
                type="button"
                className={`ce-tab ${day === d ? 'is-on' : ''}`}
                onClick={() => setDay(d)}
              >
                {shortDate(d, i18n.language)}
                <span className="ce-tab__n">{(planning[d] || []).length}</span>
              </button>
            ))}
          </div>
        )}

        {slots.length === 0 && <p className="ce-note">{t('noRunSheet')}</p>}

        {slots.map((slot) => (editSlot && editSlot.id === slot.id ? (
          <div key={slot.id} className="bravos-form">
            <input
              type="text"
              value={editSlot.title}
              maxLength={100}
              onChange={(e) => setEditSlot({ ...editSlot, title: e.target.value })}
            />
            <div className="bravos-form__row">
              <label className="bravos-form__label">
                {t('startTime')}
                <input
                  type="datetime-local"
                  value={editSlot.start || ''}
                  onChange={(e) => setEditSlot(withStart(editSlot, e.target.value))}
                />
              </label>
              <label className="bravos-form__label">
                {t('endTime')}
                <input
                  type="datetime-local"
                  value={editSlot.end || ''}
                  onChange={(e) => setEditSlot({ ...editSlot, end: e.target.value })}
                />
              </label>
            </div>
            <div className="bravos-form__row">
              <button
                type="button"
                className="bravos-submit"
                disabled={busy || !editSlot.title.trim()}
                onClick={() => { const s = editSlot; setEditSlot(null);
                  act(apiRequest('PUT', `/company/event/${eventId}/planning/${s.id}`,
                                 { title: s.title, start: s.start, end: s.end })); }}
              >
                {t('save')}
              </button>
              <button type="button" className="bravos-submit bravos-submit--ghost"
                      onClick={() => setEditSlot(null)}>
                {t('cancel')}
              </button>
            </div>
          </div>
        ) : (
          <div key={slot.id} className="hv-slot">
            <span className="hv-slot__time">
              {wallClockTime(slot.start)}
              {slot.end ? `–${wallClockTime(slot.end)}` : ''}
            </span>
            <span className="hv-slot__title">{slot.title}</span>
            {/* A slot whose name comes from the activity catalogue is not the customer's to
                rename — the name belongs to the game, and other events use it. */}
            {!slot.is_activity && (
              <>
                <button type="button" className="ce-person__act"
                        onClick={() => setEditSlot({ ...slot })}>
                  {t('edit')}
                </button>
                <button
                  type="button"
                  className="ce-person__act"
                  disabled={busy}
                  onClick={() => act(apiRequest('DELETE',
                    `/company/event/${eventId}/planning/${slot.id}`))}
                >
                  {t('delete')}
                </button>
              </>
            )}
          </div>
        )))}

        {draftSlot ? (
          <div className="bravos-form">
            <input
              type="text"
              value={draftSlot.title}
              maxLength={100}
              placeholder={t('runSheetItemPlaceholder')}
              onChange={(e) => setDraftSlot({ ...draftSlot, title: e.target.value })}
            />
            <div className="bravos-form__row">
              <label className="bravos-form__label">
                {t('startTime')}
                <input
                  type="datetime-local"
                  value={draftSlot.start}
                  onChange={(e) => setDraftSlot(withStart(draftSlot, e.target.value))}
                />
              </label>
              <label className="bravos-form__label">
                {t('endTime')}
                <input
                  type="datetime-local"
                  value={draftSlot.end}
                  onChange={(e) => setDraftSlot({ ...draftSlot, end: e.target.value })}
                />
              </label>
            </div>
            <div className="bravos-form__row">
              <button
                type="button"
                className="bravos-submit"
                disabled={busy || !draftSlot.title.trim() || !draftSlot.start}
                onClick={() => { const s = draftSlot; setDraftSlot(null);
                  act(apiRequest('POST', `/company/event/${eventId}/planning`, s)); }}
              >
                {t('save')}
              </button>
              <button type="button" className="bravos-submit bravos-submit--ghost"
                      onClick={() => setDraftSlot(null)}>
                {t('cancel')}
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            className="bravos-submit bravos-submit--ghost"
            onClick={() => setDraftSlot(nextSlot(day, slots, event))}
          >
            <Icon name="plus" /> {t('addRunSheetItem')}
          </button>
        )}

        {/* Writing a programme is not the same job as changing one during the event. Several
            days at once, hours only, one line under the other — that screen, from here. */}
        <button
          type="button"
          className="bravos-submit bravos-submit--ghost"
          onClick={() => navigate(`/host-event/${eventId}/run-sheet`)}
        >
          <Icon name="calendar" /> {t('multiDayEvent')}
        </button>

        {/* ★★ The pack. This is the screen somebody is standing on at the venue, so it is where the
            paper belongs: the run sheet with its heats, a sheet per team, a score slip per heat,
            the standings and the points ladder — one document, in the order a day is run. Nothing
            else on this screen survives a flat battery. */}
        <button
          type="button"
          className="bravos-submit bravos-submit--ghost"
          onClick={() => downloadApiFile(`/company/event/${eventId}/print/pack.pdf`,
            { lang: i18n.language })}
        >
          <Icon name="print" /> {t('printPackButton')}
        </button>
        <p className="ce-check__hint mt-1">{t('printPackHint')}</p>
      </Card>

      {/* --- surveys ----------------------------------------------------------------------- */}
      <Card>
        <Title my="1">
          <Icon name="document" theme="primary" className="me-2" />
          {t('hostSurveys')}
        </Title>
        <p className="bravos-q__hint mb-2">{t('hostSurveysNote')}</p>

        {surveys.length === 0 && <p className="ce-note">{t('noSurveysYet')}</p>}

        {/* ★ What "after" MEANS. The platform cannot know when the room has had enough time to
            form an opinion, and guessing would mean chasing people we have no business
            chasing — so the host stays on the button. What changed is that the button now
            comes to them: once the event is over, the post-survey says so and opens the
            composer pre-aimed at the people who actually turned up. */}
        {afterSurvey && (
          <div className="hv-nudge">
            <p className="hv-nudge__title">
              <Icon name="comment" /> {t('askHowItWent')}
            </p>
            <p className="bravos-q__hint mb-2">
              {t('askHowItWentNote', { name: afterSurvey.name, n: counts.attended || 0 })}
            </p>
            <button
              type="button"
              className="bravos-submit"
              disabled={busy}
              onClick={() => setComposing({ surveyId: afterSurvey.id, segment: 'attended' })}
            >
              <Icon name="bell" /> {t('sendItToThem')}
            </button>
          </div>
        )}

        {surveys.map((s) => (
          <div key={s.id} className="hv-survey">
            <div className="ce-row__top">
              <span className="ce-row__name">{s.name}</span>
              {/* The phase was drawn here and settable nowhere on this screen — you had to go
                  to the library to say when your own event asks its questions. Whoever writes
                  the questions changes it in place; everyone else still reads the pill.
                  Matches the route, which needs the same two answers to re-phase. */}
              {canWriteSurveys && s.owned ? (
                <select
                  className={`ce-pill hv-phase hv-phase--${s.phase || 'none'}`}
                  value={s.phase || ''}
                  disabled={busy}
                  aria-label={t('surveyWhen')}
                  onChange={(e) => act(apiRequest('PUT',
                    `/company/event/${eventId}/surveys/${s.id}`, { phase: e.target.value }))}
                >
                  <option value="">{t('noPhase')}</option>
                  {PHASES.map((p) => (
                    <option key={p} value={p}>{t(`phase_${p}`)}</option>
                  ))}
                </select>
              ) : s.phase && (
                <span className={`ce-pill hv-phase hv-phase--${s.phase}`}>
                  <Icon name={PHASE_ICONS[s.phase]} /> {t(`phase_${s.phase}`)}
                </span>
              )}
              <span className={`ce-pill ${s.is_open ? 'ce-pill--going' : 'ce-pill--draft'}`}>
                {s.is_open ? t('surveyOpen') : t('surveyClosed')}
              </span>
              {!s.is_published && <span className="ce-pill ce-pill--draft">{t('notPublished')}</span>}
              {/* There is a quiz in here and it is worth something. Same pill, same wording as
                  the library, because the host who attached it there reads it again here. */}
              {s.points > 0 && (
                <span className="ce-pill ce-pill--going">{t('quizWorth', { n: s.points })}</span>
              )}
            </div>
            <div className="ce-row__meta">
              {t('surveyResponses', { n: s.responses })}
              {s.other_events > 0 && ` · ${t('alsoOnEvents', { n: s.other_events })}`}
              {s.fields > 0 && ` · ${t('nQuestions', { n: s.fields })}`}
              {/* ★ How many of those questions actually score, not just that some do. A set is
                  allowed to be part quiz, and "3 of 9 score" is what the host has to say out
                  loud to the room. */}
              {s.quiz_fields > 0 && ` · ${t('nScoredQuestions', { n: s.quiz_fields })}`}
            </div>

            <div className="ce-row__buttons">
              <button type="button" className="ce-person__act"
                      onClick={() => toggleResults(s.id)}>
                {openSurvey === s.id ? t('hideResults') : t('viewResults')}
              </button>
              {/* Opening and closing is a HOST power: it happens on the night, and the host is
                  the person standing there. */}
              <button
                type="button"
                className="ce-person__act"
                disabled={busy}
                onClick={() => act(apiRequest('PUT',
                  `/company/event/${eventId}/surveys/${s.id}`,
                  { action: s.is_open ? 'close' : 'release' }))}
              >
                {s.is_open ? t('closeSurvey') : t('releaseSurvey')}
              </button>
              {s.is_open && s.is_published && (
                <button
                  type="button"
                  className="ce-person__act"
                  onClick={() => setPresenting(s.id)}
                >
                  {t('presentMode')}
                </button>
              )}
              {/* Opens the composer rather than firing: the host picks who (segment + roster
                  tags), reads the message, and chooses the channels. It used to post an
                  empty body behind a confirm, so it always went to the whole segment on
                  both channels with copy nobody had seen. */}
              <button
                type="button"
                className="ce-person__act"
                disabled={busy || !(s.is_published && s.link)}
                onClick={() => setComposing({ surveyId: s.id, segment: 'survey_pending' })}
              >
                <Icon name="bell" /> {t('remindPending')}
              </button>
              {s.link && (
                <button
                  type="button"
                  className="ce-person__act"
                  onClick={() => {
                    navigator.clipboard.writeText(s.link);
                    setNotice(t('linkCopied'));
                  }}
                >
                  {t('copyLink')}
                </button>
              )}
              {canWriteSurveys && s.owned && (
                <>
                  <button
                    type="button"
                    className="ce-person__act"
                    onClick={() => navigate(`/company-surveys?open=${s.id}`)}
                  >
                    {t('editQuestions')}
                  </button>
                  <button
                    type="button"
                    className="ce-person__act"
                    disabled={busy}
                    onClick={() => act(apiRequest('DELETE',
                      `/company/event/${eventId}/surveys/${s.id}`))}
                  >
                    {t('detachSurvey')}
                  </button>
                </>
              )}
            </div>

            {openSurvey === s.id && (
              <div className="hv-results">
                <SurveyResults results={results[s.id]} t={t} />
              </div>
            )}
          </div>
        ))}

        {/* Writing a new set of questions and reusing one the company already has were one
            button, which hid the second: the library appeared only after pressing "Add", so
            "connect the survey we always use" looked like it did not exist. Two buttons, two
            flows, and BOTH ask when the survey is asked before they attach anything. */}
        {canWriteSurveys && picking === 'survey_new' && (
          <div className="bravos-form">
            <input
              type="text"
              value={newSurvey}
              maxLength={100}
              placeholder={t('newSurveyPlaceholder')}
              onChange={(e) => setNewSurvey(e.target.value)}
            />
            <PhaseChoice value={newPhase} onChange={setNewPhase} disabled={busy} t={t} />
            <div className="bravos-form__row">
              <button
                type="button"
                className="bravos-submit"
                disabled={busy || !newSurvey.trim()}
                onClick={() => { const name = newSurvey.trim(); setNewSurvey('');
                  setPicking(null);
                  act(apiRequest('POST', `/company/event/${eventId}/surveys`,
                                 { name, phase: newPhase })); }}
              >
                {t('save')}
              </button>
              <button type="button" className="bravos-submit bravos-submit--ghost"
                      onClick={() => setPicking(null)}>
                {t('cancel')}
              </button>
            </div>
          </div>
        )}

        {canWriteSurveys && picking === 'survey_existing' && (
          <div className="bravos-form">
            <PhaseChoice value={newPhase} onChange={setNewPhase} disabled={busy} t={t} />
            <p className="bravos-cal__label">{t('pickFromLibrary')}</p>
            {library === null && <span className="auth__spinner" />}
            {library !== null
              && library.filter((s) => !surveys.some((a) => a.id === s.id)).length === 0 && (
                <p className="ce-note">{t('libraryEmpty')}</p>
            )}
            {(library || []).filter((s) => !surveys.some((a) => a.id === s.id)).map((s) => (
              <button
                key={s.id}
                type="button"
                className="bravos-submit bravos-submit--ghost"
                disabled={busy}
                onClick={() => { setPicking(null);
                  act(apiRequest('POST', `/company/event/${eventId}/surveys`,
                                 { survey_id: s.id, phase: newPhase })); }}
              >
                {/* The count is how many events already ask it — the whole reason to reuse.
                    The pill says whether you are about to attach a quiz, which is not a thing
                    to discover on the night. */}
                {s.name}
                {s.points > 0 && (
                  <span className="ce-pill ce-pill--going ms-2">
                    {t('quizWorth', { n: s.points })}
                  </span>
                )}
                {' '}
                <span className="ce-tab__n">{s.events}</span>
              </button>
            ))}
            <div className="bravos-form__row">
              <button type="button" className="bravos-submit bravos-submit--ghost"
                      onClick={() => setPicking(null)}>
                {t('cancel')}
              </button>
            </div>
          </div>
        )}

        {canWriteSurveys && picking !== 'survey_new' && picking !== 'survey_existing' && (
          <div className="bravos-form__row">
            <button
              type="button"
              className="bravos-submit bravos-submit--ghost"
              onClick={() => { setNewPhase('pre'); setPicking('survey_new'); }}
            >
              <Icon name="plus" /> {t('addSurvey')}
            </button>
            <button
              type="button"
              className="bravos-submit bravos-submit--ghost"
              onClick={() => {
                setNewPhase('pre');
                setPicking('survey_existing');
                if (library === null) {
                  apiRequest('GET', '/company/surveys')
                    .then((res) => setLibrary(res.surveys || []))
                    .catch(() => setLibrary([]));
                }
              }}
            >
              <Icon name="link" /> {t('connectSurvey')}
            </button>
          </div>
        )}
      </Card>

      {/* --- breakouts ---------------------------------------------------------------------
          One press: split whoever is in the room into N groups and tell them. The pool is
          resolved on the SERVER (the people who scanned in, else everybody registered), so this
          card sends a number and nothing else — and cannot disagree with the fuller screen about
          what "the room" means. Everything that needs looking at first — correcting who is here,
          naming the groups and their rooms, moving somebody, filling it in from a spreadsheet —
          is on /breakouts, the same split the run sheet makes. */}
      <Card>
        <Title my="1">
          <Icon name="users" theme="primary" className="me-2" />
          {t('breakouts')}
        </Title>
        <p className="bravos-q__hint mb-2">{t('breakoutsCardNote')}</p>

        <div className="hv-bo__draw">
          <label className="bravos-form__label hv-bo__n">
            {t('breakoutGroups')}
            <input
              type="number"
              min="2"
              max="50"
              value={boCount}
              onChange={(e) => setBoCount(Number(e.target.value) || 2)}
            />
          </label>
          <button
            type="button"
            className="hv-bo__act"
            disabled={busy}
            onClick={() => act(apiRequest('POST', `/company/event/${eventId}/breakouts`,
                                          { group_count: boCount, notify: true }))}
          >
            <Icon name="users" /> {t('breakoutDistributeAndTell')}
          </button>
        </div>

        {breakouts.length === 0 && <p className="ce-note mt-2">{t('noBreakoutYet')}</p>}
        {breakouts.slice(0, 3).map((b) => (
          <div key={b.id} className="hv-bo__round">
            <strong className="hv-bo__name">{b.name || t('breakoutUnnamed')}</strong>
            <span className="hv-bo__when">
              {[t('breakoutNGroups', { n: b.group_count, people: b.member_count }),
                b.planning_title,
                b.is_published
                  ? t('breakoutToldAt', { time: (b.published_at || '').slice(11, 16) })
                  : t('breakoutNotToldYet')].filter(Boolean).join(' · ')}
            </span>
          </div>
        ))}

        <div className="hv-bo__draw mt-2">
          <button
            type="button"
            className="hv-bo__act"
            disabled={!breakouts.some((b) => b.is_published)}
            onClick={() => navigate(`/host-event/${eventId}/breakout-board`)}
          >
            <Icon name="megaphone" /> {t('project')}
          </button>
          <button
            type="button"
            className="hv-bo__act"
            onClick={() => navigate(`/host-event/${eventId}/breakouts`)}
          >
            <Icon name="users" /> {t('breakoutManage')}
          </button>
        </div>
      </Card>

      {/* --- the prizes -------------------------------------------------------------------
          Second to last, because it is the second to last thing that happens: the ceremony, then
          the photographs. Three numbers and a door — the gap between "decided" and "revealed" is
          the ceremony still to come, and it is the only thing about awards a host needs to see
          without opening anything. Everything else (inventing a prize, its picture, who won it,
          revealing it) is on the awards screen, which is a running order and not a card. */}
      <Card>
        <Title my="1">
          <Icon name="trophy" theme="primary" className="me-2" />
          {t('awardsTitle')}
        </Title>
        <p className="bravos-q__hint mb-2">{t('awardsCardNote')}</p>

        {awards.total > 0 ? (
          <div className="hv-counts">
            {[['awardsCountPrizes', awards.total], ['awardsCountDecided', awards.decided],
              ['awardsCountRevealed', awards.revealed]].map(([key, n]) => (
                <div key={key} className="hv-count">
                  <span className="hv-count__n">{n != null ? n : '–'}</span>
                  <span className="hv-count__label">{t(key)}</span>
                </div>
            ))}
          </div>
        ) : (
          <p className="ce-note">{t('awardsNoneYet')}</p>
        )}

        <div className="bravos-form__row mt-2">
          <button
            type="button"
            className="bravos-copy"
            onClick={() => navigate(`/company-event/${eventId}/awards`)}
          >
            <Icon name="trophy" />
            {' '}
            {awards.total > 0 ? t('awardsOpen') : t('awardsInventOne')}
          </button>
        </div>
      </Card>

      {/* --- the room: photos, the wall, the recap ------------------------------------------ */}
      {/* The same component the participants see under the event page, which is the point: the
          host moderates the wall people are actually looking at, not a copy of it. Delete, mark a
          best photo and the recap composer appear because the SERVER says this person may — the
          payload carries can_manage of its own. */}
      <Card>
        <Title my="1">
          <Icon name="camera" theme="primary" className="me-2" />
          {t('hostTheRoom')}
        </Title>
        <p className="bravos-q__hint mb-2">{t('hostTheRoomNote')}</p>
        <EventSocial eventId={eventId} />
      </Card>
    </Base>
  );
};

export default HostEvent;
